import axios from 'axios';
import { API_CONFIG } from '../config/apiConfig';
import { fileSvc } from './FileSvc';

// 文件上传服务
export class FileUploadSvc {
    /**
     * 获取token
     * @param agent
     * @param suffix
     */
    async getToken(agent, suffix) {
        try {
            const formData = new FormData();
            formData.append('agent', agent);
            formData.append('suffix', suffix);
            
            const response = await axios.post(`${API_CONFIG.baseUrl}/CRUD/CRUD-Ali-qiniuToken.do`, formData);
            
            if (response.data && response.data.success && response.data.result) {
                return response.data.result;
            } else {
                throw new Error(response.data?.message || '获取Token失败');
            }
        } catch (error) {
            console.error('FileUploadSvc.getToken error:', error);
            throw error;
        }
    }

    /**
     * 清理token数据
     * @param token
     * @returns {*}
     */
    cleanToken(token) {
        // 清理accessid，移除可能的/OSSAccessKeyId后缀
        if (token.accessid) {
            token.accessid = token.accessid.replace(/\/+OSSAccessKeyId$/, '');
        }
        return token;
    }

    /**
     * 上传文件
     * @param file
     * @param agent
     * @param options
     * @param onProgress
     */
    async upload(file, agent, options = {}, onProgress) {
        return new Promise((resolve, reject) => {
            let suffix = '';
            
            // 优先从file.name中获取文件扩展名
            if (file.name && file.name.includes('.')) {
                let nameSplit = file.name.split('.');
                if (nameSplit.length > 1) {
                    suffix = nameSplit[nameSplit.length - 1];
                }
            } 
            // 如果file.name中没有扩展名，再从file.path中获取
            else if (file.path && file.path.includes('.')) {
                let pathSplit = file.path.split('.');
                if (pathSplit.length > 1) {
                    suffix = pathSplit[pathSplit.length - 1];
                }
            }

            // 确保扩展名是小写
            suffix = suffix.toLowerCase();
            
            // 验证扩展名是否为常见图片格式
            const validImageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
            if (suffix && !validImageExtensions.includes(suffix)) {
                // 如果不是常见图片格式，默认为jpg
                suffix = 'jpg';
            }

            console.log('FileUploadSvc.upload: determined suffix', suffix);
            
            this.getToken(agent, suffix).then(token => {
                const cleanedToken = this.cleanToken(token);
                const url = cleanedToken.host + '/' + cleanedToken.key;
                
                // 调用 uploadWithXHR 方法上传文件到阿里云 OSS
                this.uploadWithXHR(file, cleanedToken, url, options, onProgress, resolve, reject);
            }).catch(error => {
                console.error('FileUploadSvc.upload: getToken error', error);
                reject(error);
            });
        });
    }

    /**
     * 上传图片
     * @param agent
     * @param onProgress
     */
    uploadImage(agent, onProgress) {
        return new Promise((resolve, reject) => {
            fileSvc.chooseImage({ count: 1 }).subscribe(
                async (files) => {
                    try {
                        const uploadPromises = files.map(file => this.upload(file, agent, {}, onProgress));
                        const results = await Promise.all(uploadPromises);
                        resolve(results);
                    } catch (error) {
                        console.error('上传图片失败:', error);
                        reject(error);
                    }
                },
                (error) => {
                    console.error('选择图片失败:', error);
                    reject(error);
                }
            );
        });
    }

    /**
     * 上传媒体文件
     * @param agent
     * @param onProgress
     */
    uploadMedia(agent, onProgress) {
        return new Promise((resolve, reject) => {
            fileSvc.chooseMedia({ count: 1 }).subscribe(
                async (files) => {
                    try {
                        const uploadPromises = files.map(file => this.upload(file, agent, {}, onProgress));
                        const results = await Promise.all(uploadPromises);
                        resolve(results);
                    } catch (error) {
                        console.error('上传媒体文件失败:', error);
                        reject(error);
                    }
                },
                (error) => {
                    console.error('选择媒体文件失败:', error);
                    reject(error);
                }
            );
        });
    }

    /**
     * 使用 XMLHttpRequest 上传文件
     * @param file
     * @param token
     * @param url
     * @param options
     * @param onProgress
     */
    uploadWithXHR(file, token, url, options, onProgress, resolve, reject) {
        // 直接使用 fallback 上传方法
        this.fallbackUpload(file, token, url, options, onProgress, resolve, reject);
    }

    /**
     *  fallback 上传方法
     * @param file
     * @param token
     * @param url
     * @param options
     * @param onProgress
     * @param resolve
     * @param reject
     */
    fallbackUpload(file, token, url, options, onProgress, resolve, reject) {
        console.log('FileUploadSvc.fallbackUpload: starting fallback upload');
        console.log('FileUploadSvc.fallbackUpload: file path', file.path);
        
        // 检查 file.path 是否是 blob URL
        if (file.path.startsWith('blob:')) {
            console.log('FileUploadSvc.fallbackUpload: file.path is blob URL, converting to file object');
            // 从 blob URL 创建 File 对象
            fetch(file.path)
                .then(response => response.blob())
                .then(blob => {
                    const fileObject = new File([blob], file.name, { type: blob.type });
                    console.log('FileUploadSvc.fallbackUpload: file object created', fileObject);
                    this.uploadFileObject(fileObject, token, url, options, onProgress, resolve, reject);
                })
                .catch(error => {
                    console.error('FileUploadSvc.fallbackUpload: failed to convert blob to file', error);
                    reject(new Error('上传失败: 无法转换 blob URL'));
                });
        } else if (file.path.startsWith('ttfile:')) {
            console.log('FileUploadSvc.fallbackUpload: file.path is ttfile URL, converting to blob');
            // 尝试将 ttfile 转换为 blob
            this.convertTtfileToBlob(file.path)
                .then(blob => {
                    const fileObject = new File([blob], file.name, { type: blob.type });
                    console.log('FileUploadSvc.fallbackUpload: file object created', fileObject);
                    this.uploadFileObject(fileObject, token, url, options, onProgress, resolve, reject);
                })
                .catch(error => {
                    console.error('FileUploadSvc.fallbackUpload: failed to convert ttfile to blob', error);
                    reject(new Error('上传失败: 无法转换 ttfile URL'));
                });
        } else {
            console.error('FileUploadSvc.fallbackUpload: unsupported file path format', file.path);
            reject(new Error('上传失败: 不支持的文件路径格式'));
        }
    }

    /**
     * 将 ttfile:// 路径转换为 blob
     * @param ttfilePath
     * @returns {Promise<Blob>}
     */
    convertTtfileToBlob(ttfilePath) {
        return new Promise((resolve, reject) => {
            console.log('FileUploadSvc.convertTtfileToBlob: converting ttfile to blob', ttfilePath);
            
            // 尝试使用飞书的 getImageInfo API 获取图片信息
            if (window.h5sdk && (window.h5sdk.tt || window.tt)) {
                const tt = window.h5sdk.tt || window.tt;
                if (tt.getImageInfo) {
                    tt.getImageInfo({
                        src: ttfilePath,
                        success: (res) => {
                            console.log('FileUploadSvc.convertTtfileToBlob: getImageInfo success', res);
                            // 尝试使用 fetch 获取 blob
                            fetch(ttfilePath)
                                .then(response => response.blob())
                                .then(blob => {
                                    console.log('FileUploadSvc.convertTtfileToBlob: fetch blob success', blob);
                                    resolve(blob);
                                })
                                .catch(error => {
                                    console.error('FileUploadSvc.convertTtfileToBlob: fetch blob failed', error);
                                    reject(error);
                                });
                        },
                        fail: (res) => {
                            console.error('FileUploadSvc.convertTtfileToBlob: getImageInfo failed', res);
                            // 即使 getImageInfo 失败，也尝试直接 fetch
                            fetch(ttfilePath)
                                .then(response => response.blob())
                                .then(blob => {
                                    console.log('FileUploadSvc.convertTtfileToBlob: fetch blob success after getImageInfo failure', blob);
                                    resolve(blob);
                                })
                                .catch(error => {
                                    console.error('FileUploadSvc.convertTtfileToBlob: fetch blob failed', error);
                                    reject(error);
                                });
                        }
                    });
                } else {
                    // 如果 getImageInfo 不可用，直接尝试 fetch
                    fetch(ttfilePath)
                        .then(response => response.blob())
                        .then(blob => {
                            console.log('FileUploadSvc.convertTtfileToBlob: fetch blob success without getImageInfo', blob);
                            resolve(blob);
                        })
                        .catch(error => {
                            console.error('FileUploadSvc.convertTtfileToBlob: fetch blob failed', error);
                            reject(error);
                        });
                }
            } else {
                console.error('FileUploadSvc.convertTtfileToBlob: not in 飞书 environment');
                reject(new Error('非飞书环境'));
            }
        });
    }

    /**
     * 上传文件对象
     * @param fileObject
     * @param token
     * @param url
     * @param options
     * @param onProgress
     * @param resolve
     * @param reject
     */
    uploadFileObject(fileObject, token, url, options, onProgress, resolve, reject) {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        
        formData.append('key', token.key);
        formData.append('policy', token.policy);
        formData.append('OSSAccessKeyId', token.accessid);
        formData.append('signature', token.signature);
        formData.append('success_action_status', 200);
        formData.append('file', fileObject);
        
        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                if (onProgress) {
                    onProgress(progress);
                }
            }
        });
        
        xhr.addEventListener('load', () => {
            console.log('FileUploadSvc.uploadFileObject: upload completed with status', xhr.status);
            if (xhr.status === 200) {
                resolve(url);
            } else {
                reject(new Error(`上传失败: HTTP ${xhr.status}`));
            }
        });
        
        xhr.addEventListener('error', (event) => {
            console.error('FileUploadSvc.uploadFileObject: upload error', event);
            reject(new Error('上传失败: 网络错误'));
        });
        
        xhr.addEventListener('timeout', () => {
            console.error('FileUploadSvc.uploadFileObject: upload timeout');
            reject(new Error('上传失败: 超时'));
        });
        
        xhr.addEventListener('abort', () => {
            console.error('FileUploadSvc.uploadFileObject: upload aborted');
            reject(new Error('上传失败: 被中止'));
        });
        
        xhr.timeout = options.timeout || 60000;
        xhr.withCredentials = false;
        xhr.open('POST', token.host);
        xhr.send(formData);
    }
}

export const fileUploadSvc = new FileUploadSvc();
